import { Stack, Text, useMantineTheme } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { IconCheck, IconClipboardText, IconForbid2, IconLoader, IconX } from '@tabler/icons-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import RootButton from '@/components/Buttons/RootButton'
import ActionNomination from '@/components/Modals/Governance/ActionNomination'
import ViewApplicationModal from '@/components/Modals/Governance/ViewApplicationModal'
import ModalSkeleton from '@/components/Modals/ModalSkeleton'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { CandidateStatus } from '@/hooks/useGovernance'
import { useAuth } from '@/plugins/auth'

import { NominationActionProps } from '../NomineesList'

/* -------------------------------------------------------------------------- */
/*                              Nomination Status                             */
/* -------------------------------------------------------------------------- */
interface NominationStatusProps {
  status: CandidateStatus
}

const NominationStatus: React.FC<NominationStatusProps> = ({ status }) => {
  const theme = useMantineTheme()
  const text = useMemo(() => {
    switch (status) {
      case 'PENDING':
        return 'Nomination Pending'
      case 'ACCEPTED':
        return 'Nomination Accepted'
      case 'REJECTED':
        return 'Nomination Rejected'
      case 'REMOVED':
        return 'Nomination Removed'
      default:
        return ''
    }
  }, [status])
  const icon = useMemo(() => {
    switch (status) {
      case 'PENDING':
        return <IconLoader color={theme.colors.textAndIcon[0]} />
      case 'ACCEPTED':
        return <IconCheck color={theme.colors.textAndIcon[0]} />
      case 'REJECTED':
        return <IconX color={theme.colors.textAndIcon[0]} />
      case 'REMOVED':
        return <IconForbid2 color={theme.colors.textAndIcon[0]} />
      default:
        return null
    }
  }, [status, theme.colors.textAndIcon])
  return (
    <Stack style={{ flexDirection: 'row', gap: '5px', alignItems: 'center', whiteSpace: 'nowrap' }}>
      <Text>{text}</Text>
      {icon}
    </Stack>
  )
}

/* -------------------------------------------------------------------------- */
/*                              Application Button                            */
/* -------------------------------------------------------------------------- */
interface ApplicationButtonProps {
  nominee: string
  status: CandidateStatus
  isBreakpoint: boolean | undefined
  isMobile: boolean | undefined
}

const ApplicationButton: React.FC<ApplicationButtonProps> = ({
  nominee,
  status,
  isBreakpoint,
  isMobile,
}) => {
  const { t } = useTranslation()
  const { address } = useAuth()
  const navigate = useNavigate()
  const { nomineeApplication, electionData } = useGovernanceContext()

  // Modals
  const [openedReject, { open: openReject, close: closeReject }] = useDisclosure()
  const [openedApplication, { open: openApplication, close: closeApplication }] =
    useDisclosure(false)

  // Application Logic
  const hasApplication = useMemo(
    () => nomineeApplication(nominee) !== '',
    [nomineeApplication, nominee]
  )

  // Nomination period ended
  const nominationActive = useMemo(() => {
    const now = new Date().getTime() / 1000
    return electionData.nominationTime < now && electionData.campaignTime > now
  }, [electionData])

  // Voting period started, so users cannot fill applications anymore or edit
  const fillApplicationPeriodEnded = useMemo(() => {
    const now = new Date().getTime() / 1000
    return electionData.campaignTime < now
  }, [electionData])

  return (
    <>
      {status === 'PENDING' && address === nominee && nominationActive ? (
        <Stack
          style={{
            flexDirection: 'row',
            width: isBreakpoint ? '100%' : '450px',
          }}
        >
          <RootButton error style1={false} onClick={openReject} expand>
            Reject {!isMobile && 'Nomination'}
          </RootButton>
          <RootButton expand style1={false} onClick={() => navigate('/application')}>
            Accept {!isMobile && 'Nomination'}
          </RootButton>
        </Stack>
      ) : (
        <Stack
          style={{
            flexDirection: isBreakpoint ? 'column' : 'row',
            gap: '15px',
            alignItems: 'center',
            width: '100%',
            justifyContent: 'flex-end',
          }}
        >
          <Stack style={{ flexDirection: 'row', width: '100%', justifyContent: 'flex-end' }}>
            {!hasApplication &&
              address === nominee &&
              status === 'ACCEPTED' &&
              !fillApplicationPeriodEnded && (
                <RootButton
                  expand={isBreakpoint}
                  style1={false}
                  leftSection={<IconClipboardText size={20} />}
                  onClick={() => navigate('/application')}
                >
                  Fill Application
                </RootButton>
              )}
            {hasApplication && status === 'ACCEPTED' && (
              <RootButton
                expand={isBreakpoint}
                style1={false}
                onClick={openApplication}
                leftSection={<IconClipboardText size={20} />}
              >
                {address === nominee && !fillApplicationPeriodEnded ? 'Edit' : 'View'} Application
              </RootButton>
            )}
          </Stack>
        </Stack>
      )}
      <ModalSkeleton opened={openedReject} onClose={closeReject} title="Reject Nomination">
        <ActionNomination close={closeReject} action="reject" candidate={nominee} />
      </ModalSkeleton>
      <ModalSkeleton
        title={t('components.governance.modal.acceptNomination.title', 'View Application')}
        onClose={closeApplication}
        opened={openedApplication}
      >
        <ViewApplicationModal
          nominee={nominee}
          close={closeApplication}
          fillApplicationPeriodEnded={fillApplicationPeriodEnded}
        />
      </ModalSkeleton>
    </>
  )
}

/* -------------------------------------------------------------------------- */
/*                              Nomination Action                             */
/* -------------------------------------------------------------------------- */

const NominationAction: React.FC<NominationActionProps> = ({
  nominee,
  status,
  isMobile,
  isBreakpoint,
}) => {
  const { address } = useAuth()
  const myNominationIsPending = useMemo(
    () => status === 'PENDING' && address === nominee,
    [status, address, nominee]
  )
  return (
    <>
      <Stack
        style={{
          flexDirection: isBreakpoint ? 'column' : 'row',
          justifyContent: isBreakpoint ? 'center' : 'flex-end',
          alignItems: 'center',
        }}
      >
        <ApplicationButton
          nominee={nominee}
          status={status}
          isBreakpoint={isBreakpoint}
          isMobile={isMobile}
        />
        {!myNominationIsPending && <NominationStatus status={status} />}
      </Stack>
    </>
  )
}

export default NominationAction
